import { TFunction } from "i18next";
import { COUNTRIES } from "../../../../constants/countries";

/**
 * MENSAJES DE ERROR POR PAÍS
 * 
 * Este archivo centraliza los mensajes de error de validación según el país.
 * Cada función retorna el mensaje de error apropiado basado en el código de país.
 * 
 * ESCALABILIDAD:
 * - Para agregar un nuevo país, solo agrega un nuevo case en cada función
 * - Agrega las traducciones correspondientes en translation.json
 * - No necesitas modificar la lógica de validación en validations.ts
 * 
 * EJEMPLO DE USO:
 * const errorMsg = getDocumentErrorMessage(countryCode, t, documentType);
 * setErrors({ ...errors, document: errorMsg });
 * 
 * BENEFICIOS:
 * ✅ Escalable: Fácil agregar nuevos países sin tocar validaciones
 * ✅ Mantenible: Mensajes centralizados en un solo lugar
 * ✅ Limpio: Sin condicionales dispersos por el código
 */

interface ErrorMessages {
  documentInvalid: string;
  documentPrefixInvalid?: string;
}

export const getErrorMessages = (
  countryCode: string,
  t: TFunction<"translation", undefined>
): ErrorMessages => {
  switch (countryCode) {
    case COUNTRIES.DO: // República Dominicana
      return {
        documentInvalid: t("branches.create.errors.cedula"),
        // RD no tiene validación de prefijos
      };

    case COUNTRIES.AR: // Argentina
    default:
      return {
        documentInvalid: t("branches.create.errors.cuit-cuil"),
        documentPrefixInvalid: t("branches.create.errors.cuit-cuil-physical"), // o cuit-cuil-legal según el tipo
      };
  }
};

/**
 * Obtiene el mensaje de error del documento según el país y tipo
 */
export const getDocumentErrorMessage = (
  countryCode: string,
  t: TFunction<"translation", undefined>,
  documentType?: "CUIT" | "CUIL" | "CEDULA"
): string => {
  switch (countryCode) {
    case COUNTRIES.DO: // República Dominicana
      return t("branches.create.errors.cedula");

    // Ejemplo para agregar México en el futuro:
    // case COUNTRIES.MX:
    //   return t("branches.create.errors.rfc");

    // Ejemplo para agregar Chile en el futuro:
    // case COUNTRIES.CL:
    //   return t("branches.create.errors.rut");

    case COUNTRIES.AR: // Argentina
    default:
      return t("branches.create.errors.cuit-cuil");
  }
};

/**
 * Obtiene el mensaje de error de prefijo según el país y tipo de documento
 */
export const getDocumentPrefixErrorMessage = (
  countryCode: string,
  t: TFunction<"translation", undefined>,
  documentType: "CUIT" | "CUIL" | "CEDULA"
): string | null => {
  switch (countryCode) {
    case COUNTRIES.DO: // República Dominicana
      // República Dominicana no valida prefijos en la cédula
      return null;

    // Ejemplo para México (RFC tiene validaciones específicas):
    // case COUNTRIES.MX:
    //   if (documentType === "RFC_FISICA") {
    //     return t("branches.create.errors.rfc-fisica-format");
    //   } else if (documentType === "RFC_MORAL") {
    //     return t("branches.create.errors.rfc-moral-format");
    //   }
    //   return null;

    case COUNTRIES.AR: // Argentina
    default:
      if (documentType === "CUIL") {
        return t("branches.create.errors.cuit-cuil-physical");
      } else if (documentType === "CUIT") {
        return t("branches.create.errors.cuit-cuil-legal");
      }
      return null;
  }
};

